import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Loader2, Search, Plus, X, Trash2, ArrowRight, Factory, Building2,
  Landmark, User, CircleDollarSign, Users, Briefcase, Megaphone, Package,
  AlertTriangle, Sparkles, GitBranch,
} from "lucide-react";

const FLOW_META = {
  biens_services: { label: "Biens & services", icon: Package, color: "#378ADD" },
  capital: { label: "Capital", icon: CircleDollarSign, color: "#1D9E75" },
  talent: { label: "Talent", icon: Users, color: "#7F77DD" },
  mandats: { label: "Mandats", icon: Briefcase, color: "#EF9F27" },
  influence: { label: "Influence", icon: Megaphone, color: "#E24B4A" },
};

const TYPE_ICON = { societe: Building2, institution: Landmark, personne: User };

/* Vue chaîne de valeur réutilisable — utilisable dans l'Annuaire, un drawer, etc. */
export const SplcChainView = ({ initialEntityId, onExit }) => {
  const [center, setCenter] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async (entityId) => {
    setLoading(true);
    try {
      const r = await api.get(`/workspace/annuaire/splc/${entityId}`);
      setData(r.data);
      setCenter(r.data.center);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (initialEntityId) load(initialEntityId); }, [initialEntityId, load]);

  const suggest = async () => {
    setSuggesting(true);
    setSuggestions(null);
    try {
      const r = await api.post(`/workspace/annuaire/splc/${center.id}/suggest`);
      setSuggestions(r.data.suggestions);
      if (!r.data.suggestions.length) notify.info("L'IA n'a pas trouvé de lien plausible parmi vos entités.");
    } catch (e) { notify.error(fmtError(e)); }
    finally { setSuggesting(false); }
  };

  const acceptSuggestion = async (s) => {
    try {
      const payload = s.direction === "supplier"
        ? { supplier_id: s.entity_id, customer_id: center.id, flow_type: s.flow_type }
        : { supplier_id: center.id, customer_id: s.entity_id, flow_type: s.flow_type };
      await api.post("/workspace/annuaire/splc/links", payload);
      setSuggestions((arr) => arr.filter((x) => x !== s));
      await load(center.id);
    } catch (e) { notify.error(fmtError(e)); }
  };

  if (!center) {
    return <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-zinc-500" /></div>;
  }

  const CenterIcon = TYPE_ICON[center.type] || Building2;

  return (
    <div className="space-y-4" data-testid="splc-view">
      {loading && <div className="text-center py-2"><Loader2 className="w-4 h-4 animate-spin inline text-zinc-500" /></div>}
      {(data?.alerts || []).map((a, i) => (
        <div key={i} className="flex items-start gap-2 p-3 rounded-md border border-[#EF9F27]/30 bg-[#EF9F27]/10 text-[#EF9F27] text-xs" data-testid={`splc-alert-${a.kind}`}>
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{a.message}</span>
        </div>
      ))}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 items-start">
        <ChainColumn
          side="supplier"
          title="Amont — lui fournissent"
          items={data?.suppliers || []}
          onNavigate={(id) => load(id)}
          onDelete={async (linkId) => {
            await api.delete(`/workspace/annuaire/splc/links/${linkId}`);
            await load(center.id);
          }}
          onAdd={() => setAdding("supplier")}
        />
        <div className="flex flex-col items-center gap-2 lg:pt-10">
          <div className="hidden lg:flex items-center gap-1 text-zinc-600"><ArrowRight className="w-4 h-4" /></div>
          <div className="noam-card px-6 py-5 text-center border-[#378ADD]/40 min-w-[220px]" data-testid="splc-center-card">
            <CenterIcon className="w-6 h-6 mx-auto text-[#378ADD] mb-2" />
            <div className="text-white font-semibold text-sm">{center.name}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{center.sector || center.type}</div>
            {onExit && (
              <button onClick={onExit} className="mt-3 text-[10px] text-zinc-500 hover:text-white underline underline-offset-2" data-testid="splc-change-center">
                Changer d'acteur
              </button>
            )}
            <button onClick={suggest} disabled={suggesting} className="mt-2 w-full btn-ghost text-[11px] justify-center disabled:opacity-50" data-testid="splc-suggest-btn" title="NOAM analyse la description de l'entité et propose des liens plausibles parmi vos entités existantes">
              {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Suggérer des liens (IA)
            </button>
          </div>
          <div className="hidden lg:flex items-center gap-1 text-zinc-600"><ArrowRight className="w-4 h-4" /></div>
          <div className="flex flex-wrap justify-center gap-1.5 max-w-[240px]">
            {Object.entries(FLOW_META).map(([k, m]) => (
              <span key={k} className="flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full border border-white/10" style={{ color: m.color }}>
                <m.icon className="w-2.5 h-2.5" /> {m.label}
              </span>
            ))}
          </div>
        </div>
        <ChainColumn
          side="customer"
          title="Aval — il alimente"
          items={data?.customers || []}
          onNavigate={(id) => load(id)}
          onDelete={async (linkId) => {
            await api.delete(`/workspace/annuaire/splc/links/${linkId}`);
            await load(center.id);
          }}
          onAdd={() => setAdding("customer")}
        />
      </div>

      {suggestions && suggestions.length > 0 && (
        <div className="noam-card p-4 space-y-2" data-testid="splc-suggestions-panel">
          <div className="flex items-center gap-2 text-xs font-semibold text-white">
            <Sparkles className="w-3.5 h-3.5 text-[#7F77DD]" /> Liens suggérés par l'IA — à valider
          </div>
          {suggestions.map((s, i) => {
            const flow = FLOW_META[s.flow_type] || FLOW_META.biens_services;
            return (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.03] border border-white/5" data-testid={`splc-suggestion-${i}`}>
                <span className="text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold shrink-0" style={{ background: `${flow.color}1f`, color: flow.color }}>
                  {s.direction === "supplier" ? "Amont" : "Aval"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{s.entity_name} <span className="text-[10px] text-zinc-500">· {flow.label}</span></div>
                  <div className="text-[11px] text-zinc-500 truncate">{s.reason}</div>
                </div>
                <button onClick={() => acceptSuggestion(s)} className="btn-primary text-[11px] py-1 px-2.5 shrink-0" data-testid={`splc-suggestion-accept-${i}`}>Ajouter</button>
                <button onClick={() => setSuggestions((arr) => arr.filter((x) => x !== s))} className="p-1 rounded text-zinc-500 hover:text-white shrink-0" data-testid={`splc-suggestion-dismiss-${i}`}><X className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
        </div>
      )}

      {adding && (
        <AddLinkModal
          side={adding}
          center={center}
          onClose={() => setAdding(null)}
          onSaved={async () => { setAdding(null); await load(center.id); }}
        />
      )}
    </div>
  );
};

/* Drawer universel — ouvrable depuis n'importe quel module (Origination, Positionnement, Graphe…).
   Accepte un entityId direct OU un entityName à résoudre dans l'Annuaire. */
export const SplcDrawer = ({ entityId, entityName, onClose }) => {
  const [resolvedId, setResolvedId] = useState(entityId || null);
  const [notFound, setNotFound] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (entityId || !entityName) return;
    (async () => {
      try {
        const r = await api.get("/workspace/annuaire/entities", { params: { search: entityName } });
        const list = Array.isArray(r.data) ? r.data : (r.data.items || r.data.entities || []);
        const exact = list.find((e) => e.name.toLowerCase() === entityName.toLowerCase()) || list[0];
        if (exact) setResolvedId(exact.id);
        else setNotFound(true);
      } catch { setNotFound(true); }
    })();
  }, [entityId, entityName]);

  const createAndTrace = async () => {
    setCreating(true);
    try {
      const r = await api.post("/workspace/annuaire/entities", { name: entityName, type: "societe", status: "prospect" });
      setResolvedId(r.data.id);
      setNotFound(false);
      notify.success(`« ${entityName} » ajouté à l'Annuaire.`);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="noam-card w-full max-w-5xl my-6" onClick={(e) => e.stopPropagation()} data-testid="splc-drawer">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline flex items-center gap-1.5"><GitBranch className="w-3 h-3 rotate-90" /> SPLC — Chaîne de valeur</div>
            <div className="noam-display text-base font-semibold mt-0.5">{entityName || "Acteur"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5" data-testid="splc-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">
          {notFound ? (
            <div className="text-center py-8 space-y-3" data-testid="splc-drawer-notfound">
              <p className="text-sm text-zinc-400">« {entityName} » n'est pas encore dans votre Annuaire.</p>
              <button onClick={createAndTrace} disabled={creating} className="btn-primary text-xs disabled:opacity-50" data-testid="splc-drawer-create">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Créer dans l'Annuaire et tracer sa chaîne
              </button>
            </div>
          ) : resolvedId ? (
            <SplcChainView initialEntityId={resolvedId} />
          ) : (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-zinc-500" /></div>
          )}
        </div>
      </div>
    </div>
  );
};

/* Bouton compact réutilisable pour ouvrir le drawer SPLC depuis n'importe quel module. */
export const SplcButton = ({ entityId, entityName, className = "", testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={className || "text-[10px] px-2 py-1 rounded bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/25 inline-flex items-center gap-1"}
        title="Chaîne de valeur SPLC : fournisseurs à gauche, clients à droite"
        data-testid={testid || "splc-open-btn"}
      >
        <GitBranch className="w-3 h-3 rotate-90" /> SPLC
      </button>
      {open && <SplcDrawer entityId={entityId} entityName={entityName} onClose={() => setOpen(false)} />}
    </>
  );
};

/* Vue Annuaire (par défaut) : sélecteur d'acteur + chaîne. */
export const AnnuaireSPLC = () => {
  const [pickedId, setPickedId] = useState(null);

  if (!pickedId) {
    return (
      <div className="noam-card p-8 max-w-xl mx-auto space-y-4" data-testid="splc-picker-panel">
        <div>
          <div className="noam-overline">SPLC — Chaîne de valeur</div>
          <h2 className="noam-display text-lg font-semibold mt-1">Choisissez l'acteur central</h2>
          <p className="text-xs text-zinc-500 mt-1">
            À gauche : ceux qui l'alimentent (fournisseurs, capital, talent). À droite : ceux qu'il alimente (clients, mandats).
            Cliquez sur un maillon pour naviguer de proche en proche.
          </p>
        </div>
        <EntitySearch onPick={(e) => setPickedId(e.id)} autoFocus testid="splc-center-search" />
      </div>
    );
  }
  return <SplcChainView initialEntityId={pickedId} onExit={() => setPickedId(null)} />;
};

function ChainColumn({ side, title, items, onNavigate, onDelete, onAdd }) {
  return (
    <div className="space-y-2" data-testid={`splc-column-${side}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title} ({items.length})</div>
        <button onClick={onAdd} className="btn-ghost text-[11px] py-1 px-2" data-testid={`splc-add-${side}`}>
          <Plus className="w-3 h-3" /> Ajouter
        </button>
      </div>
      {items.length === 0 && (
        <div className="noam-card p-5 text-center text-xs text-zinc-600 border-dashed">
          Aucun lien {side === "supplier" ? "amont" : "aval"} pour l'instant.
        </div>
      )}
      {items.map((it) => {
        const flow = FLOW_META[it.flow_type] || FLOW_META.biens_services;
        const Icon = TYPE_ICON[it.entity.type] || Factory;
        return (
          <div key={it.link_id} className="noam-card p-3 flex items-center gap-3 group hover:border-white/20 transition-colors" data-testid={`splc-node-${it.entity.id}`}>
            <Icon className="w-4 h-4 shrink-0" style={{ color: flow.color }} />
            <button onClick={() => onNavigate(it.entity.id)} className="flex-1 min-w-0 text-left" data-testid={`splc-navigate-${it.entity.id}`} title="Centrer la chaîne sur cet acteur">
              <div className="text-sm text-white truncate group-hover:text-[#378ADD] transition-colors">{it.entity.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">
                <span style={{ color: flow.color }}>{it.label || flow.label}</span>
                {it.entity.sector ? ` · ${it.entity.sector}` : ""}
                {it.degree > 1 ? ` · ${it.degree} liens` : ""}
              </div>
            </button>
            <button onClick={() => onDelete(it.link_id)} className="opacity-0 group-hover:opacity-100 p-1 rounded text-zinc-600 hover:text-[#E24B4A] transition-opacity" data-testid={`splc-delete-${it.link_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function AddLinkModal({ side, center, onClose, onSaved }) {
  const [picked, setPicked] = useState(null);
  const [flow, setFlow] = useState("biens_services");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const payload = side === "supplier"
        ? { supplier_id: picked.id, customer_id: center.id, flow_type: flow, label }
        : { supplier_id: center.id, customer_id: picked.id, flow_type: flow, label };
      const r = await api.post("/workspace/annuaire/splc/links", payload);
      if (r.data.already_existed) notify.info("Ce lien existe déjà.");
      onSaved();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="splc-add-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">Chaîne de valeur</div>
            <div className="noam-display text-base font-semibold mt-0.5">
              {side === "supplier" ? `Qui alimente ${center.name} ?` : `Qui ${center.name} alimente-t-il ?`}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {picked ? (
            <div className="flex items-center justify-between noam-card p-3">
              <div className="text-sm text-white">{picked.name}</div>
              <button onClick={() => setPicked(null)} className="text-[11px] text-zinc-500 hover:text-white underline">changer</button>
            </div>
          ) : (
            <EntitySearch onPick={setPicked} excludeId={center.id} autoFocus testid="splc-link-search" />
          )}
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Type de flux</label>
            <select value={flow} onChange={(e) => setFlow(e.target.value)} className="noam-input" data-testid="splc-flow-select">
              {Object.entries(FLOW_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Précision (optionnel)</label>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex : composants batteries, mandat de gérance…" className="noam-input" data-testid="splc-label-input" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
            <button onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button onClick={save} disabled={!picked || saving} className="btn-primary text-xs disabled:opacity-50" data-testid="splc-link-save">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Créer le lien"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EntitySearch({ onPick, excludeId, autoFocus, testid }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.get("/workspace/annuaire/entities", { params: q ? { search: q } : {} });
        const list = Array.isArray(r.data) ? r.data : (r.data.items || r.data.entities || []);
        setRows(list.filter((e) => e.id !== excludeId).slice(0, 12));
      } catch { setRows([]); }
      finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, excludeId]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher une entité de l'Annuaire…"
          className="noam-input pl-9"
          autoFocus={autoFocus}
          data-testid={testid}
        />
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1">
        {busy && <Loader2 className="w-4 h-4 animate-spin text-zinc-600 mx-auto" />}
        {rows.map((e) => (
          <button key={e.id} onClick={() => onPick(e)}
            className="w-full text-left px-3 py-2 rounded-md hover:bg-white/5 transition-colors"
            data-testid={`splc-pick-${e.id}`}>
            <span className="text-sm text-white">{e.name}</span>
            <span className="text-[10px] text-zinc-500 ml-2">{e.sector || e.type}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default AnnuaireSPLC;
